import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 34. RELATIONS ENTRE ENTITÉS
// Système générique pour relier les entités entre elles, de façon
// cohérente et réutilisable par tous les modules.
//   Customer ↔ Company · Employee ↔ Department · Service ↔ Professional
//   Product ↔ Business · Booking ↔ Customer/Service/Business
//   Document ↔ Customer/Project …
// ─────────────────────────────────────────────────────────────

export const CORE_ENTITY_TYPES: { key: string; label: string; icon: string }[] = [
  { key: 'customer', label: 'Client', icon: 'ri-user-star-line' },
  { key: 'contact', label: 'Contact', icon: 'ri-contacts-line' },
  { key: 'business', label: 'Entreprise', icon: 'ri-building-2-line' },
  { key: 'organization', label: 'Organisation', icon: 'ri-building-4-line' },
  { key: 'employee', label: 'Employé', icon: 'ri-team-line' },
  { key: 'department', label: 'Département', icon: 'ri-node-tree' },
  { key: 'professional', label: 'Professionnel', icon: 'ri-user-2-line' },
  { key: 'service', label: 'Service', icon: 'ri-service-line' },
  { key: 'product', label: 'Produit', icon: 'ri-shopping-bag-line' },
  { key: 'booking', label: 'Réservation', icon: 'ri-calendar-check-line' },
  { key: 'order', label: 'Commande', icon: 'ri-shopping-bag-3-line' },
  { key: 'document', label: 'Document', icon: 'ri-file-text-line' },
  { key: 'project', label: 'Projet', icon: 'ri-folders-line' },
  { key: 'location', label: 'Établissement', icon: 'ri-map-pin-line' },
];

export function coreEntityMeta(key: string | null | undefined) {
  return CORE_ENTITY_TYPES.find((e) => e.key === key) || null;
}

/** Types de relation courants proposés par défaut (relation libre ensuite). */
export const RELATION_TYPES: { key: string; label: string }[] = [
  { key: 'related', label: 'Lié à' },
  { key: 'belongs_to', label: 'Appartient à' },
  { key: 'belongs_to_company', label: 'Client d’une entreprise' },
  { key: 'works_at', label: 'Travaille pour' },
  { key: 'member_of', label: 'Membre de' },
  { key: 'offers', label: 'Propose' },
  { key: 'booked', label: 'A réservé' },
  { key: 'ordered', label: 'A commandé' },
  { key: 'assigned_to', label: 'Assigné à' },
];

export function relationTypeMeta(key: string | null | undefined) {
  return RELATION_TYPES.find((r) => r.key === key) || RELATION_TYPES[0];
}

export interface CoreEntityRelation {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  source_type: string;
  source_id: string;
  target_type: string;
  target_id: string;
  relation_type: string | null;
  label: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string | null;
}

export async function listEntityRelations(
  organizationId: number,
  entityType: string,
  entityId: string | number,
): Promise<CoreEntityRelation[]> {
  const { data, error } = await supabase
    .from('core_entity_relations')
    .select('*')
    .eq('organization_id', organizationId)
    .or(`and(source_type.eq.${entityType},source_id.eq.${entityId}),and(target_type.eq.${entityType},target_id.eq.${entityId})`)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreEntityRelation[];
}

export async function listAllEntityRelations(organizationId: number): Promise<CoreEntityRelation[]> {
  const { data, error } = await supabase
    .from('core_entity_relations')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreEntityRelation[];
}

export interface RelationInput {
  tenantId: number | null;
  organizationId: number;
  sourceType: string;
  sourceId: string | number;
  targetType: string;
  targetId: string | number;
  relationType?: string;
  label?: string | null;
}

export async function createEntityRelation(input: RelationInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_entity_relations')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      source_type: input.sourceType,
      source_id: String(input.sourceId),
      target_type: input.targetType,
      target_id: String(input.targetId),
      relation_type: input.relationType || 'related',
      label: input.label || null,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function deleteEntityRelation(id: number): Promise<void> {
  const { error } = await supabase.from('core_entity_relations').delete().eq('id', id);
  if (error) throw error;
}

export interface RelationStats {
  total: number;
  byRelationType: Record<string, number>;
  byPair: Record<string, number>;
}

export async function fetchRelationStats(organizationId: number): Promise<RelationStats> {
  const { data } = await supabase
    .from('core_entity_relations')
    .select('relation_type, source_type, target_type')
    .eq('organization_id', organizationId);
  const byRelationType: Record<string, number> = {};
  const byPair: Record<string, number> = {};
  (data || []).forEach((r) => {
    const row = r as { relation_type: string | null; source_type: string; target_type: string };
    const rt = row.relation_type || 'related';
    byRelationType[rt] = (byRelationType[rt] || 0) + 1;
    const pair = `${row.source_type} → ${row.target_type}`;
    byPair[pair] = (byPair[pair] || 0) + 1;
  });
  return { total: (data || []).length, byRelationType, byPair };
}